import { repositoryTreeSchema } from '../_data/schemas.js';
import { createLocalSchemaRegistry } from './localSchemaRegistry.js';
import { compileSchema, schemaId } from './schemaCompilation.js';
const treeValidator = compileSchema(repositoryTreeSchema);
const pathValidator = treeValidator.fragment('#/$defs/path');
const escapePointer = (value) => value.replace(/~/g, '~0').replace(/\//g, '~1');
const treeSource = { filePath: '.', scope: '.', schema: true };
function message(error) {
    return error instanceof Error ? error.message : String(error);
}
function issue(source, kind, repositoryPath, keyword, text, schemaPath = '') {
    return { kind, filePath: source.filePath, scope: source.scope, repositoryPath, schemaPath, keyword, message: text };
}
function fromAjv(source, kind, repositoryPath, error) {
    return {
        ...issue(source, kind, repositoryPath, error.keyword, error.message ?? error.keyword, error.schemaPath),
        instancePath: error.instancePath,
        params: error.params,
    };
}
function repositoryPathAt(root, pointer) {
    let value = root;
    let path = root.path;
    for (const token of pointer.split('/').slice(1)) {
        if (value === null || typeof value !== 'object')
            break;
        value = value[token.replace(/~1/g, '/').replace(/~0/g, '~')];
        if (value && typeof value === 'object' && 'nodeKind' in value && 'path' in value && typeof value.path === 'string') {
            path = value.path;
        }
    }
    return path;
}
/** Validates only tree structure and schema constraints; annotations never inspect file contents. */
export function validateScopedTree(options) {
    const issues = [];
    const nodes = new Map();
    const structure = treeValidator.validate(options.tree);
    if (!structure.valid) {
        issues.push(...structure.issues.map(error => fromAjv(treeSource, 'tree', '.', error)));
    }
    else {
        const visit = (node, expected, pointer) => {
            if (node.path !== expected) {
                issues.push(issue(treeSource, 'tree', expected, 'path', `Expected canonical path '${expected}', received '${node.path}'.`, `${pointer}/path`));
            }
            nodes.set(expected, node);
            if (node.nodeKind === 'directory') {
                for (const [name, child] of Object.entries(node.children)) {
                    visit(child, expected === '.' ? name : `${expected}/${name}`, `${pointer}/children/${escapePointer(name)}`);
                }
            }
        };
        visit(options.tree, '.', '');
    }
    let registry;
    try {
        registry = createLocalSchemaRegistry(options.registryOptions);
    }
    catch (error) {
        issues.push(issue(treeSource, 'schema', '.', 'registry', message(error)));
        return { valid: false, issues };
    }
    const invalid = new Set();
    const entries = [
        ...(options.references ?? []).map(schema => ({ filePath: schemaId(schema) ?? '.', scope: '.', schema })),
        ...options.schemas,
    ];
    for (const source of entries) {
        try {
            const result = registry.validateSchema(source.schema);
            if (!result.valid) {
                invalid.add(source.schema);
                issues.push(...result.issues.map(error => fromAjv(source, 'schema', source.scope, error)));
            }
        }
        catch (error) {
            invalid.add(source.schema);
            issues.push(issue(source, 'schema', source.scope, 'schema', message(error)));
        }
    }
    // Registration is a separate pass: forward and mutually recursive references are available at compilation.
    for (const source of entries) {
        if (invalid.has(source.schema))
            continue;
        try {
            if (schemaId(source.schema))
                registry.register(source.schema);
            else if (!options.schemas.includes(source))
                throw new Error('Reference schemas require an $id.');
        }
        catch (error) {
            invalid.add(source.schema);
            issues.push(issue(source, 'schema', source.scope, 'registration', message(error)));
        }
    }
    for (const source of options.schemas) {
        let target;
        if (!pathValidator.validate(source.scope).valid) {
            issues.push(issue(source, 'scope', source.scope, 'scope', 'Scope must be a canonical repository-relative directory path.'));
        }
        else if (structure.valid) {
            target = nodes.get(source.scope);
            if (!target)
                issues.push(issue(source, 'scope', source.scope, 'scope', `Scope '${source.scope}' does not exist in the repository tree.`));
            else if (target.nodeKind !== 'directory') {
                issues.push(issue(source, 'scope', source.scope, 'scope', `Scope '${source.scope}' is not a directory.`));
                target = undefined;
            }
        }
        if (invalid.has(source.schema))
            continue;
        try {
            const validator = registry.compile(source.schema);
            if (!target)
                continue;
            const result = validator.validate(target);
            issues.push(...result.issues.map(error => fromAjv(source, 'constraint', repositoryPathAt(target, error.instancePath), error)));
        }
        catch (error) {
            issues.push(issue(source, 'schema', source.scope, 'compilation', message(error)));
        }
    }
    return { valid: issues.length === 0, issues };
}
